import { useReadContract, useReadContracts, useChainId, useAccount } from 'wagmi'
import { defaultChainId, isSupportedNetwork } from '@/config/wagmi'
import { getNFTContract } from '@/config/contract'
import { formatEther } from 'viem'

export interface NFTData {
  tokenId: bigint
  eligible: boolean
  refundAmount: string
  refundAmountFormatted: string
  timeLeft: bigint
  timeLeftFormatted: string
  tokenURI: string
}

/**
 * Hook to fetch user's NFTs and refund eligibility
 */
export const useUserNFTs = (address: `0x${string}` | undefined) => {
  const { isConnected } = useAccount()
  const chainIdFromWallet = useChainId()
  const chainId = isConnected && isSupportedNetwork(chainIdFromWallet || 0)
    ? (chainIdFromWallet as number)
    : defaultChainId
  const contract = getNFTContract(chainId)
  
  // Get user's token IDs
  const { data: tokenIds, isLoading: isLoadingTokens } = useReadContract({
    ...contract,
    functionName: 'tokensOfOwner',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address,
      refetchInterval: 30000,
    },
  })

  // Fetch refund eligibility and metadata for each token
  const tokenIdsArray = (tokenIds || []) as bigint[]
  
  const { data: refundData, isLoading: isLoadingRefund } = useReadContracts({
    contracts: tokenIdsArray.flatMap((tokenId) => [
      {
        ...contract,
        functionName: 'checkRefundEligibility',
        args: [tokenId],
      },
      {
        ...contract,
        functionName: 'tokenURI',
        args: [tokenId],
      },
    ]),
    query: {
      enabled: tokenIdsArray.length > 0,
      refetchInterval: 30000,
    },
  })
 
  // Use a loosely-typed alias to avoid deep generic instantiation issues
  const typedRefundData = refundData as any[] | undefined
 
  // Parse the data
  const nfts: NFTData[] = []
  
  if (typedRefundData && tokenIdsArray.length > 0) {
    for (let i = 0; i < tokenIdsArray.length; i++) {
      const tokenId = tokenIdsArray[i]
      const eligibilityData = typedRefundData[i * 2]
      const uriData = typedRefundData[i * 2 + 1]

      if (eligibilityData.result && uriData.result) {
        const [eligible, refundAmount, timeLeft] = eligibilityData.result as [boolean, bigint, bigint]
        
        // Format time left
        const timeLeftSeconds = Number(timeLeft)
        const days = Math.floor(timeLeftSeconds / 86400)
        const hours = Math.floor((timeLeftSeconds % 86400) / 3600)
        const minutes = Math.floor((timeLeftSeconds % 3600) / 60)
        
        let timeLeftFormatted = ''
        if (days > 0) {
          timeLeftFormatted = `${days}d ${hours}h`
        } else if (hours > 0) {
          timeLeftFormatted = `${hours}h ${minutes}m`
        } else if (minutes > 0) {
          timeLeftFormatted = `${minutes}m`
        } else {
          timeLeftFormatted = 'Expired'
        }

        nfts.push({
          tokenId,
          eligible,
          refundAmount: refundAmount.toString(),
          refundAmountFormatted: formatEther(refundAmount),
          timeLeft,
          timeLeftFormatted,
          tokenURI: uriData.result as unknown as string,
        })
      }
    }
  }
 
  const isLoading = (isLoadingTokens || isLoadingRefund) as boolean
 
  return {
    nfts,
    isLoading,
    hasNFTs: nfts.length > 0,
  }
}

/**
 * Hook to check if user can mint more
 */
export const useCanMint = (
  address: `0x${string}` | undefined,
  quantity: number
) => {
  const { isConnected } = useAccount()
  const chainIdFromWallet = useChainId()
  const chainId = isConnected && isSupportedNetwork(chainIdFromWallet || 0)
    ? (chainIdFromWallet as number)
    : defaultChainId
  const contract = getNFTContract(chainId)
  
  const { data: numberMinted } = useReadContract({
    ...contract,
    functionName: 'numberMinted',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address,
    },
  })

  const { data: maxPerWallet } = useReadContract({
    ...contract,
    functionName: 'MAX_PER_WALLET',
  })
 
  // If data is not yet loaded, optimistically allow mint but
  // report remaining as 0 to avoid showing incorrect limits.
  // IMPORTANT: we must not treat 0n as "no data" because 0n is a
  // valid value when the user hasn't minted anything yet.
  if (numberMinted === undefined || maxPerWallet === undefined) {
    return {
      canMint: true,
      remaining: 0,
      reason: '',
    }
  }
 
  const remaining = Number(maxPerWallet) - Number(numberMinted)
  const canMint = remaining >= quantity

  return {
    canMint,
    remaining,
    reason: canMint ? '' : `You can only mint ${remaining} more NFT(s)`,
  }
}
